import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Landmark, Building2, FolderKanban, Info } from 'lucide-react';

const fmt = v => `\u20B9${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/**
 * TDS ledger — what the studio has withheld from vendor payments and must
 * deposit with the government, quarter by quarter (Indian FY). Toggle between
 * vendor-wise (PAN-wise, the shape 26Q needs) and project-wise, because the
 * same vendor team works across multiple projects.
 */
export default function TdsLedgerPanel() {
  const { api } = useAuth();
  const [groupBy, setGroupBy] = useState('vendor');
  const [fy, setFy] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    const q = `?group_by=${groupBy}${fy ? `&fy=${fy}` : ''}`;
    api.get(`/accounting/tds-summary${q}`).then(r => {
      setData(r.data);
      if (!fy && r.data?.fy) setFy(r.data.fy);
    }).catch(() => setData({ rows: [], totals: null }));
  }, [api, groupBy, fy]);

  const rows = data?.rows || [];
  const t = data?.totals;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md" data-testid="tds-ledger-panel">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Landmark className="w-4 h-4 text-rose-400" />
          <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">TDS Pendency (26Q)</h3>
          {t && (
            <span className="text-[10px] bg-rose-500/15 text-rose-300 px-2 py-0.5 rounded-full font-bold" data-testid="tds-to-deposit-chip">
              {fmt(t.tds_deducted)} to deposit
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden">
            <button onClick={() => setGroupBy('vendor')} data-testid="tds-group-vendor"
              className={`text-[10px] px-2.5 py-1 flex items-center gap-1 transition-colors ${groupBy === 'vendor' ? 'bg-rose-500 text-zinc-950 font-bold' : 'text-zinc-400 hover:text-white'}`}>
              <Building2 className="w-3 h-3" /> Vendor-wise
            </button>
            <button onClick={() => setGroupBy('project')} data-testid="tds-group-project"
              className={`text-[10px] px-2.5 py-1 flex items-center gap-1 transition-colors ${groupBy === 'project' ? 'bg-rose-500 text-zinc-950 font-bold' : 'text-zinc-400 hover:text-white'}`}>
              <FolderKanban className="w-3 h-3" /> Project-wise
            </button>
          </div>
          <select value={fy} onChange={e => setFy(e.target.value)} data-testid="tds-fy-select"
            className="bg-zinc-950 border border-zinc-800 rounded-sm text-[10px] text-zinc-300 px-2 py-1 outline-none">
            {(data?.available_fys || []).map(f => <option key={f} value={f}>FY {f}</option>)}
          </select>
        </div>
      </div>

      {!data ? (
        <p className="px-4 py-8 text-center text-xs text-zinc-600">Loading TDS ledger…</p>
      ) : rows.length === 0 ? (
        <p className="px-4 py-8 text-center text-xs text-zinc-600" data-testid="tds-empty">No TDS activity in FY {fy || data.fy}.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-zinc-950 text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2 font-normal">{groupBy === 'vendor' ? 'Vendor / Party · PAN' : 'Project'}</th>
                <th className="text-right px-2 py-2 font-normal">Inv</th>
                <th className="text-right px-2 py-2 font-normal">Taxable</th>
                {(data.quarters || []).map(q => (
                  <th key={q.key} className="text-right px-2 py-2 font-normal whitespace-nowrap" title={`TDS return due ${q.return_due}`}>{q.label}</th>
                ))}
                <th className="text-right px-2 py-2 font-normal text-rose-300">To deposit</th>
                <th className="text-right px-3 py-2 font-normal">On open inv.</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.key} className="border-t border-zinc-800/50 hover:bg-zinc-900/50" data-testid={`tds-row-${r.key}`}>
                  <td className="px-3 py-2">
                    <p className="text-zinc-200 truncate max-w-[240px]">{r.name}</p>
                    <p className="text-[9px] text-zinc-600">
                      {groupBy === 'vendor'
                        ? (r.pan ? `PAN ${r.pan}` : 'PAN missing — add it on the vendor profile')
                        : (r.counterparts || []).slice(0, 3).join(', ')}
                    </p>
                  </td>
                  <td className="px-2 py-2 text-right text-zinc-400 font-mono">{r.invoice_count}</td>
                  <td className="px-2 py-2 text-right text-zinc-400 font-mono">{fmt(r.taxable)}</td>
                  {(data.quarters || []).map(q => (
                    <td key={q.key} className="px-2 py-2 text-right font-mono">
                      {r.quarters?.[q.key]?.tds_deducted > 0
                        ? <span className="text-rose-300">{fmt(r.quarters[q.key].tds_deducted)}</span>
                        : <span className="text-zinc-700">—</span>}
                    </td>
                  ))}
                  <td className="px-2 py-2 text-right font-mono text-rose-400 font-bold">{fmt(r.tds_deducted)}</td>
                  <td className="px-3 py-2 text-right font-mono text-amber-400/80">{r.tds_upcoming > 0 ? fmt(r.tds_upcoming) : <span className="text-zinc-700">—</span>}</td>
                </tr>
              ))}
              {t && (
                <tr className="border-t border-zinc-700 bg-zinc-950/60 font-bold" data-testid="tds-total-row">
                  <td className="px-3 py-2 text-zinc-300 uppercase text-[10px] tracking-wider">Total · FY {data.fy}</td>
                  <td className="px-2 py-2 text-right text-zinc-300 font-mono">{t.invoice_count}</td>
                  <td className="px-2 py-2 text-right text-zinc-300 font-mono">{fmt(t.taxable)}</td>
                  {(data.quarters || []).map(q => (
                    <td key={q.key} className="px-2 py-2 text-right text-rose-300 font-mono">{fmt(t.quarters?.[q.key]?.tds_deducted)}</td>
                  ))}
                  <td className="px-2 py-2 text-right text-rose-400 font-mono">{fmt(t.tds_deducted)}</td>
                  <td className="px-3 py-2 text-right text-amber-400/80 font-mono">{fmt(t.tds_upcoming)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="px-4 py-2 border-t border-zinc-800 flex items-start gap-2">
        <Info className="w-3 h-3 text-zinc-600 mt-0.5 shrink-0" />
        <p className="text-[10px] text-zinc-600">
          {data?.deposit_rule || 'TDS deducted in a month must be deposited by the 7th of the following month.'}
          {' '}Quarterly 26Q returns due{' '}
          {(data?.quarters || []).map(q => `${q.key} ${q.return_due}`).join(' · ')}.
          {' '}"To deposit" = TDS withheld on invoices already paid. "On open inv." = TDS that will be withheld when pending invoices are paid.
        </p>
      </div>
    </div>
  );
}
